import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/Navbar";
import TokenCard from "@/components/TokenCard";
import PageTransition from "@/components/PageTransition";
import WalletModal from "@/components/WalletModal";
import { EVM_TOKENS, SOL_TOKENS } from "@/data/tokens";
import { fmtNum, getWallet } from "@/lib/wallet";
import { Search, Info, Zap } from "lucide-react";

const META = {
  evm: {
    label: "EVM Network",
    list: EVM_TOKENS,
    accent: "var(--evm)",
    grad: "grad-evm-text",
    glow: "btn-glow-evm",
    bgClass: "mesh-evm",
    tagline: "Liquid staking on Ethereum, Arbitrum, Polygon, BNB and Optimism.",
    network: "EVM",
  },
  solana: {
    label: "Solana Network",
    list: SOL_TOKENS,
    accent: "var(--sol)",
    grad: "grad-sol-text",
    glow: "btn-glow-sol",
    bgClass: "mesh-sol",
    tagline: "Low-latency liquid staking. Native MEV smoothing via Jito-aligned validators.",
    network: "SOLANA",
  },
};

export default function ChainPage({ chain }) {
  const meta = META[chain];
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("apr");
  const [wallet, setWallet] = useState(null);
  const [walletModal, setWalletModal] = useState({ open: false, target: null, reason: "" });

  useEffect(() => {
    const w = getWallet();
    setWallet(w);
    const sync = () => setWallet(getWallet());
    window.addEventListener("stk-wallet-change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("stk-wallet-change", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const tokens = useMemo(() => {
    let arr = meta.list.filter(t =>
      t.symbol.toLowerCase().includes(q.toLowerCase()) ||
      t.name.toLowerCase().includes(q.toLowerCase())
    );
    if (sort === "apr") arr = arr.sort((a, b) => b.apr - a.apr);
    if (sort === "tvl") arr = arr.sort((a, b) => b.tvl - a.tvl);
    if (sort === "az")  arr = arr.sort((a, b) => a.symbol.localeCompare(b.symbol));
    return arr;
  }, [meta.list, q, sort]);

  const onStake = (token) => {
    // Navigate to standalone HTML staking page using the new chain-prefixed URL.
    window.location.href = `${chain}/index.html?symbol=${token.symbol}`;
  };

  const openPortfolio = () => {
    // Always send to the standalone HTML/CSS/JS portfolio page.
    // The HTML page itself handles the disconnected state (wallet-connect cards).
    window.location.href = "portfolio/index.html";
  };

  const ctaLabel = !wallet
    ? "Connect Wallet & Stake Now"
    : wallet.chain !== chain
    ? "Switch Wallet & Stake Now"
    : "Stake Now →";

  const totalTvl = meta.list.reduce((a, b) => a + b.tvl, 0);
  const avgApr = (meta.list.reduce((a, b) => a + b.apr, 0) / meta.list.length).toFixed(2);
  const topApr = Math.max(...meta.list.map(t => t.apr)).toFixed(2);

  return (
    <PageTransition>
      <Navbar accent={chain} />

      <section className="relative">
        <div className={`absolute inset-0 -z-10 ${meta.bgClass}`} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-8 sm:pb-10">
          <div className="text-[11px] uppercase tracking-[0.3em] mb-4 sm:mb-5" style={{ color: meta.accent }}>
            ▍ {meta.network} STAKING
          </div>
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none">
                {meta.label.split(" ")[0]} <span className={meta.grad}>{meta.label.split(" ")[1]}</span>
              </h1>
              <p className="mt-3 sm:mt-4 max-w-xl text-sm sm:text-base" style={{ color: "var(--text-2)" }}>{meta.tagline}</p>
            </div>
            <button
              onClick={openPortfolio}
              data-testid="open-portfolio-btn"
              className="px-4 sm:px-5 h-10 sm:h-11 inline-flex items-center text-xs sm:text-sm font-bold border"
              style={{ borderColor: "var(--border-strong)", color: "#fff" }}
            >
              MY PORTFOLIO →
            </button>
          </div>

          <div className="border-t border-b mt-8 sm:mt-10" style={{ borderColor: "var(--border)" }}>
            <div className="grid grid-cols-2 md:grid-cols-4">
              <Stat label={`${meta.network} Assets`} value={meta.list.length} />
              <Stat label="Total Value Locked"      value={`$${fmtNum(totalTvl, 2)}`} />
              <Stat label="Average APR"             value={`${avgApr}%`} />
              <Stat label="Top APR"                 value={`${topApr}%`} accent={meta.accent} />
            </div>
          </div>
        </div>
      </section>

      {/* APR explainer banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-6 sm:mb-8">
        <div className="border flex items-start gap-3 p-4 relative overflow-hidden" style={{ borderColor: "var(--border)", background: "var(--surface-1)" }}>
          <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: chain === "evm" ? "linear-gradient(90deg, rgba(0,85,255,0.05), transparent 50%)" : "linear-gradient(90deg, rgba(153,69,255,0.06), transparent 50%)" }} />
          <Info size={16} className="relative mt-0.5 flex-shrink-0" style={{ color: meta.accent }} />
          <div className="relative text-xs sm:text-sm" style={{ color: "var(--text-2)" }}>
            <span className="text-white font-medium">Why is the APR higher than native?</span>{" "}
            All rewards route through our 34-validator partner mesh and the MEV smoothing pool, then
            auto-compound back into your position every 12 hours.
            {" "}
            <span className="grad-boost-text font-bold">24h Boost pools</span> capture priority-auction fees that don't fit the standard cycle — yield can be 4-8x higher.
          </div>
        </div>
      </section>

      {/* Controls */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-center gap-3 mb-5 sm:mb-6">
          <div className="flex items-center gap-2 px-3 h-10 border flex-1 min-w-[240px]" style={{ borderColor: "var(--border)", background: "var(--surface-1)" }}>
            <Search size={14} style={{ color: "var(--text-3)" }} />
            <input
              type="text"
              placeholder="Search token..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="token-search-input"
              className="bg-transparent border-0 outline-none flex-1 text-sm placeholder:text-neutral-600"
            />
          </div>
          <div className="flex items-center border" style={{ borderColor: "var(--border)" }}>
            {[
              { id: "apr", label: "APR ↓" },
              { id: "tvl", label: "TVL ↓" },
              { id: "az",  label: "A → Z" },
            ].map(s => (
              <button
                key={s.id}
                onClick={() => setSort(s.id)}
                data-testid={`sort-${s.id}-btn`}
                className="px-4 h-10 text-xs uppercase tracking-wider border-r last:border-r-0 transition-colors"
                style={{
                  borderColor: "var(--border)",
                  background: sort === s.id ? "var(--surface-3)" : "transparent",
                  color: sort === s.id ? "#fff" : "var(--text-2)",
                }}
              >{s.label}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 pb-16 sm:pb-24" data-testid="token-grid">
          {tokens.map((t, i) => (
            <TokenCard
              key={t.symbol}
              token={{ ...t, chain }}
              accent={meta.accent}
              accentGrad={meta.grad}
              accentGlow={meta.glow}
              onStake={() => onStake({ ...t, chain })}
              ctaLabel={ctaLabel}
              index={i}
            />
          ))}
          {tokens.length === 0 && (
            <div className="col-span-full text-center py-20 text-sm" style={{ color: "var(--text-3)" }}>
              No tokens match "{q}"
            </div>
          )}
        </div>
      </section>
    </PageTransition>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="px-2 sm:px-6 py-5 border-r last:border-r-0" style={{ borderColor: "var(--border)" }}>
      <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-3)" }}>{label}</div>
      <div className="font-mono font-bold text-2xl sm:text-3xl mt-1" style={{ color: accent || "#fff" }}>{value}</div>
    </div>
  );
}
